import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ActivityItem } from '@/components/ActivityItem';
import { LoadMore } from '@/components/LoadMore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivityFeed } from '@/hooks/useActivityFeed';

function ActivitySkeleton() {
  return (
    <li className="flex gap-3 py-3">
      <Skeleton className="mt-0.5 size-8 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-5 rounded-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <Skeleton className="h-3.5 w-5/6" />
      </div>
    </li>
  );
}

interface ActivityFeedProps {
  /**
   * Cap the rendered items and replace infinite scroll with a "full feed"
   * link — used for the homepage tab preview. Pages fetched here are
   * shared through the query cache with `/feed`, so the full page opens
   * warm.
   */
  maxItems?: number;
}

/**
 * The ecosystem activity feed — one minimal row per action (articles,
 * forks, edits, publications, stars, highlights), newest first, loading
 * older history as the reader scrolls.
 */
export function ActivityFeed({ maxItems }: ActivityFeedProps) {
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useActivityFeed();
  const visible = maxItems ? items.slice(0, maxItems) : items;

  if (isLoading) {
    return (
      <ol className="divide-y divide-border">
        {Array.from({ length: 8 }).map((_, i) => (
          <ActivitySkeleton key={i} />
        ))}
      </ol>
    );
  }

  if (visible.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="mx-auto max-w-sm text-muted-foreground">
            No activity yet. Try checking your relay connections, or be the first to write, star,
            or highlight something!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <ol className="divide-y divide-border">
        {visible.map((item) => (
          <ActivityItem key={item.id} item={item} />
        ))}
      </ol>

      {maxItems ? (
        (hasNextPage || items.length > maxItems) && (
          <div className="mt-4 flex justify-end">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/feed">
                Full feed
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        )
      ) : (
        <LoadMore
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          loadingContent={
            <ol className="divide-y divide-border">
              {Array.from({ length: 3 }).map((_, i) => (
                <ActivitySkeleton key={i} />
              ))}
            </ol>
          }
          endContent={
            items.length > 10 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                You're all caught up — that's all the activity your relays know about.
              </p>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
